import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import debounce from 'lodash/debounce';
import { v4 as uuid } from 'uuid';
import { GetStaffResponse, ListStaffRequest } from '@vyce/core/src/api/types';

import { NotificationContext } from '../../../contexts/notificationContext';
import { appointTeamManager, deleteTeamManager } from '../../../api/teams';
import { useForm } from 'react-hook-form';

const TEAM_RESOURCE_KEYS = ['teams', 'team'];

const extractTeamResourceIds = (resources: Record<string, string[]> | undefined): string[] => {
  if (!resources) return [];
  for (const [key, value] of Object.entries(resources)) {
    if (TEAM_RESOURCE_KEYS.includes(key.toLowerCase())) {
      return value ?? [];
    }
  }
  return [];
};

type Props = {
  selectedCompanyId: string;
  teamId: string;
  getStaffRequest: (
    companyId: string,
    payload: ListStaffRequest
  ) => Promise<AxiosResponse<GetStaffResponse>>;
  setSignalForRerender: React.Dispatch<React.SetStateAction<string>>;
};

export const useAddNewSiteManager = ({
  selectedCompanyId,
  teamId,
  getStaffRequest,
  setSignalForRerender,
}: Props) => {
  const [staff, setStaff] = useState<{ uuid: string; name: string; avatar?: string; ni_number?: string }[]>(
    []
  );
  const [staffSubstring, setStaffSubstring] = useState('');
  const [managers, setManagers] = useState<{ uuid: string; name: string; avatar?: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const { handleServerError } = useContext(NotificationContext);

  const methods = useForm<{
    teamManagers: {
      uuid: string;
      name: string;
      avatar?: string;
      ni_number?: string;
    }[];
  }>({ defaultValues: { teamManagers: managers } });

  const addManager = async (id: string) => {
    try {
      await appointTeamManager({
        companyId: selectedCompanyId,
        teamId: teamId,
        userId: id,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const deleteManager = async (id: string) => {
    try {
      await deleteTeamManager({
        companyId: selectedCompanyId,
        teamId: teamId,
        userId: id,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const syncManagers = async () => {
    if (!teamId) return;
    const values = methods.getValues();

    const initialSet = new Set(managers.map(item => item.uuid));
    const currentSet = new Set(values.teamManagers.map(item => item.uuid));

    const toAdd = Array.from(currentSet).filter(x => !initialSet.has(x));
    const toRemove = Array.from(initialSet).filter(x => !currentSet.has(x));

    setSaving(true);
    try {
      const addPromises = toAdd.map(id => addManager(id));
      const removePromises = toRemove.map(id => deleteManager(id));

      await Promise.all([...addPromises, ...removePromises]);
      await fetchTeamManagers();
      setSignalForRerender(uuid());
    } finally {
      setSaving(false);
    }
  };

  const fetchStaff = useCallback(
    async (substring: string) => {
      if (!selectedCompanyId) return;
      try {
        setLoading(true);
        const { data } = await getStaffRequest(selectedCompanyId, {
          limit: 1000,
          offset: 0,
          substring,
          roles: ['Teams:Manager'],
        });
        setStaff(
          data.items.map(item => ({
            uuid: item.user_id,
            name: `${item.first_name} ${item.last_name}`,
            avatar: item.avatar,
            ni_number: item.ni_number,
          }))
        );
        setLoading(false);
      } catch (e) {
        setLoading(false);
        handleServerError(e);
      }
    },
    [selectedCompanyId]
  );

  const fetchTeamManagers = useCallback(async () => {
    if (!selectedCompanyId || !teamId) return;
    try {
      const { data } = await getStaffRequest(selectedCompanyId, {
        limit: 1000,
        offset: 0,
        roles: ['Teams:Manager'],
      });
      const teamMembers = data.items.filter(item => extractTeamResourceIds(item.resources).includes(teamId));
      setManagers(
        teamMembers.map(item => ({
          uuid: item.user_id,
          name: `${item.first_name} ${item.last_name}`,
          avatar: item.avatar ?? item.photo?.url,
        }))
      );
    } catch (e) {
      console.error(e);
    }
  }, [selectedCompanyId, teamId, getStaffRequest]);

  const fillFormRegardingSeletedManagers = useCallback(() => {
    methods.reset({ teamManagers: managers });
    methods.setValue('teamManagers', managers, { shouldDirty: false, shouldTouch: false });
  }, [managers, methods]);

  const resetManagers = () => {
    fillFormRegardingSeletedManagers();
  };

  const staffWithManagers = useMemo(() => {
    const staffUuids = new Set(staff.map(item => item.uuid));
    const extraManagers = managers.filter(item => !staffUuids.has(item.uuid));
    return [...staff, ...extraManagers];
  }, [staff, managers]);

  const debouncedFetchTags = useMemo(() => debounce(fetchStaff, 500), [fetchStaff]);

  useEffect(() => {
    debouncedFetchTags(staffSubstring);
  }, [staffSubstring, debouncedFetchTags]);

  useEffect(() => {
    fetchTeamManagers();
  }, [fetchTeamManagers]);

  useEffect(() => {
    fillFormRegardingSeletedManagers();
  }, [fillFormRegardingSeletedManagers]);

  return {
    loading,
    saving,
    staff: staffWithManagers,
    managers,
    methods,
    resetManagers,
    syncManagers,
    setStaffSubstring,
  };
};
